import React, { useState, useEffect } from 'react';
import Badge from '@/components/common/Badge';
import Card, { CardBody } from '@/components/common/Card';
import ConfirmDialog, { DeleteIcon } from '@/components/common/ConfirmDialog';
import { formatCurrency } from '@/utils/formatters';
import { FaWrench, FaClock, FaShieldAlt, FaTag, FaPen, FaTrash } from 'react-icons/fa';
import { normalizeDataObject } from '../../utils/normalizationUtils';

/**
 * Componente para mostrar un servicio individual
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.servicio - Datos del servicio
 * @param {Array} props.sucursales - Lista de sucursales disponibles
 * @param {Array} props.categorias - Lista de categorías disponibles
 * @param {function} props.onEdit - Función para editar el servicio
 * @param {function} props.onDelete - Función para eliminar el servicio
 * @returns {JSX.Element} - Componente de ítem de servicio
 */
const ServicioItem = ({ servicio, sucursales = [], categorias = [], onEdit, onDelete }) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Normalizar datos del servicio para manejar cualquier formato
  const normalizedServicio = normalizeDataObject(servicio, 'servicio');
  
  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Comprobar inicialmente
    checkMobile();
    
    // Actualizar cuando cambie el tamaño de la ventana
    window.addEventListener('resize', checkMobile);
    
    // Limpiar al desmontar
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Función para obtener el nombre de la categoría por ID
  const getCategoriaNombre = () => {
    const categoriaId = normalizedServicio.categoriaId || normalizedServicio.categoria_id;
    if (!categoriaId) return null;
    const categoria = categorias.find(cat => cat.id === categoriaId);
    return categoria ? categoria.nombre : null;
  };
  
  // Función para obtener el color de borde según la categoría
  const getBorderColor = () => {
    const categoriaNombre = getCategoriaNombre();
    switch (categoriaNombre) {
      case 'Motor':
        return 'border-red-500';
      case 'Frenos':
        return 'border-yellow-500';
      case 'Suspensión':
        return 'border-blue-500';
      case 'Eléctrico':
        return 'border-purple-500';
      case 'Diagnóstico':
        return 'border-green-500';
      case 'Climatización':
        return 'border-cyan-500';
      default:
        return 'border-gray-500';
    }
  };
  
  // Obtener nombres de sucursales donde está disponible
  const getSucursalesNombres = () => {
    const detallesPorSucursal = normalizedServicio.detallesPorSucursal || normalizedServicio.DetallesPorSucursal;
    if (!detallesPorSucursal || !Array.isArray(detallesPorSucursal)) {
      return [];
    }
    
    return detallesPorSucursal
      .filter(detalle => {
        const disponible = detalle.disponible !== false && detalle.Disponible !== false;
        return disponible;
      })
      .map(detalle => {
        const sucursalId = detalle.sucursal ? detalle.sucursal[0] : (detalle.Sucursal ? detalle.Sucursal[0] : null);
        const sucursal = sucursales.find(s => s.id === sucursalId);
        return sucursal ? sucursal.Nombre : 'Desconocida';
      });
  };
  
  const sucursalesDisponibles = getSucursalesNombres();
  
  // Determinar estado de disponibilidad para el badge
  const getEstadoBadge = () => {
    const disponible = normalizedServicio.disponible !== false && normalizedServicio.Disponible !== false;
    return disponible ? 
      { label: 'Disponible', variant: 'success' } : 
      { label: 'No disponible', variant: 'danger' };
  };
  
  const estadoBadge = getEstadoBadge();
  
  return (
    <>
      <Card 
        hover 
        className={`mb-3 border-l-4 ${getBorderColor()} transition-all duration-300`}
      >
        <CardBody className="p-0">
          {isMobile ? (
            // Versión móvil
            <div>
              <div className="p-4">
                {/* Nombre del servicio */}
                <div className="flex items-center mb-3">
                  <h3 className="text-lg font-semibold">{normalizedServicio.nombre || normalizedServicio.Nombre}</h3>
                  <Badge 
                    variant={estadoBadge.variant} 
                    size="sm"
                    className="ml-2"
                  >
                    {estadoBadge.label}
                  </Badge>
                </div>
                {/* Badges de categoría y otros */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {getCategoriaNombre() && (
                    <Badge variant="primary" size="sm">{getCategoriaNombre()}</Badge>
                  )}
                </div>
                
                {/* Información del servicio */}
                <div className="text-sm">
                  <div className="flex items-center mb-2">
                    <FaTag className="mr-2 text-gray-500" />
                    <span className="text-gray-300">
                      Precio: {(normalizedServicio.precio || normalizedServicio.Precio) !== null
                        ? formatCurrency(normalizedServicio.precio || normalizedServicio.Precio)
                        : 'No disponible'}
                    </span>
                  </div>
                  
                  <div className="flex items-center mb-2">
                    <FaClock className="mr-2 text-gray-500" />
                    <span className="text-gray-300">
                      Duración: {(normalizedServicio.tiempoEstimado || normalizedServicio.TiempoEstimado)
                        ? `${normalizedServicio.tiempoEstimado || normalizedServicio.TiempoEstimado} min` 
                        : 'No especificado'}
                    </span>
                  </div>
                  
                  <div className="flex items-center">
                    <FaShieldAlt className="mr-2 text-gray-500" />
                    <span className="text-gray-300">
                      Garantía: {(normalizedServicio.garantia || normalizedServicio.Garantia)
                        ? `${normalizedServicio.garantia || normalizedServicio.Garantia} días` 
                        : 'No especificado'}
                    </span>
                  </div>
                  
                  {/* Sucursales donde está disponible */}
                  {sucursalesDisponibles.length > 0 && (
                    <div className="mt-2 text-gray-400 text-xs">
                      Disponible en: {sucursalesDisponibles.join(', ')}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Botones móviles */}
              <div className="flex justify-between border-t border-gray-700 bg-gray-800">
                <button
                  onClick={() => onEdit(servicio)}
                  className="flex-1 py-3 flex flex-col items-center justify-center text-yellow-500"
                  title="Editar"
                >
                  <FaPen className="text-lg" />
                  <span className="text-xs mt-1">Editar</span>
                </button>
                
                <button
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="flex-1 py-3 flex flex-col items-center justify-center text-red-500"
                  title="Eliminar"
                >
                  <FaTrash className="text-lg" />
                  <span className="text-xs mt-1">Eliminar</span>
                </button>
              </div>
            </div>
          ) : (
            // Versión escritorio
            <div className="flex justify-between items-center p-4">
              {/* Info principal */}
              <div className="flex flex-col md:flex-row w-full">
                {/* Nombre y badges */}
                <div className="flex flex-col md:flex-row md:items-center min-w-[220px] w-full md:w-1/2 mb-2 md:mb-0">
                  <div className="flex items-center mb-2 md:mb-0">
                    <div className="mr-2 bg-blue-600 bg-opacity-20 p-2 rounded-full">
                      <FaWrench className="text-blue-400" />
                    </div>
                    <h3 className="text-lg font-semibold mr-4">{normalizedServicio.nombre || normalizedServicio.Nombre}</h3>
                    <Badge 
                      variant={estadoBadge.variant} 
                      size="sm"
                      className="ml-2"
                    >
                      {estadoBadge.label}
                    </Badge>
                    {getCategoriaNombre() && (
                      <Badge variant="primary" size="sm" className="ml-2">{getCategoriaNombre()}</Badge>
                    )}
                    {/* Aquí puedes agregar más badges si lo deseas */}
                  </div>
                </div>
                {/* Datos de precio, garantía y sucursales */}
                <div className="flex flex-col md:flex-row md:items-center w-full md:w-1/2 text-sm gap-x-12">
                  <div className="flex items-center mb-1 md:mb-0">
                    <FaTag className="mr-2 text-gray-500" />
                    <span className="text-gray-300">
                      Precio: {(normalizedServicio.precio || normalizedServicio.Precio) !== null 
                        ? formatCurrency(normalizedServicio.precio || normalizedServicio.Precio) 
                        : 'No disponible'}
                    </span>
                  </div>
                  <div className="flex items-center mb-1 md:mb-0">
                    <FaShieldAlt className="mr-2 text-gray-500" />
                    <span className="text-gray-300">
                      Garantía: {(normalizedServicio.garantia || normalizedServicio.Garantia)
                        ? `${normalizedServicio.garantia || normalizedServicio.Garantia} días` 
                        : 'No especificado'}
                    </span>
                  </div>
                  {sucursalesDisponibles.length > 0 && (
                    <div className="text-gray-400 text-xs flex items-center">
                      Disponible en: {sucursalesDisponibles.join(', ')}
                    </div>
                  )}
                </div>
              </div>
              {/* Botones pequeños en línea horizontal */}
              <div className="flex flex-col md:flex-row justify-start gap-2 ml-0 md:ml-4">
                <button
                  onClick={() => onEdit(servicio)}
                  className="text-white bg-yellow-600 hover:bg-yellow-700 px-2 py-1 rounded"
                  title="Editar"
                >
                  <FaPen />
                </button>
                <button
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded"
                  title="Eliminar"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
      {/* Diálogo de confirmación para eliminar */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => {
          onDelete(normalizedServicio.id || servicio.id);
          setIsDeleteDialogOpen(false);
        }}
        title="Eliminar servicio"
        message={`¿Estás seguro de que deseas eliminar el servicio "${normalizedServicio.nombre || normalizedServicio.Nombre}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        confirmVariant="danger"
        icon={<DeleteIcon />}
      />
    </>
  );
};

export default ServicioItem;